import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";

/**
 * QL-261 / QL-262 — the parts of the loaded flow instance that standalone mode
 * needs, held in ONE store at the app root.
 *
 * The flow-instance page and the process page publish the instance metadata
 * they already load through {@link useStandaloneInstanceSource}, so
 * `useStandaloneMode()` can prefer the persisted `internalRedirectUrl` (set by
 * the partner system over the public API) over the link-carried query param
 * without every consumer having to fetch the instance itself. The store lives
 * at the root — not around the page subtree — because the navigation guard
 * (`StandaloneNavigationGuard`) sits above the routes and needs the same
 * return target to send a blocked navigation there. Outside the flow pages the
 * value is null and the hook falls back to the URL alone.
 */
export type StandaloneInstance = {
  internalRedirectUrl: string | null;
};

type StandaloneInstanceStore = {
  instance: StandaloneInstance | null;
  publish: (instance: StandaloneInstance | null) => void;
};

const StandaloneInstanceContext = createContext<StandaloneInstanceStore | null>(null);

/** Mounted once, inside the router and above every route. */
export function StandaloneInstanceProvider({ children }: { children: ReactNode }) {
  const [instance, setInstance] = useState<StandaloneInstance | null>(null);
  const value = useMemo<StandaloneInstanceStore>(
    () => ({ instance, publish: setInstance }),
    [instance],
  );
  return (
    <StandaloneInstanceContext.Provider value={value}>
      {children}
    </StandaloneInstanceContext.Provider>
  );
}

/** The instance data published by the flow pages, or null outside them. */
export function useStandaloneInstance(): StandaloneInstance | null {
  return useContext(StandaloneInstanceContext)?.instance ?? null;
}

/**
 * Publish the loaded instance's `internalRedirectUrl` to the store for as long
 * as the calling page is mounted. Pass `undefined` while the metadata is still
 * loading (nothing is published), `null` once loaded without a return target.
 */
export function useStandaloneInstanceSource(internalRedirectUrl: string | null | undefined): void {
  const publish = useContext(StandaloneInstanceContext)?.publish;
  useEffect(() => {
    if (!publish || internalRedirectUrl === undefined) return;
    publish({ internalRedirectUrl });
    return () => publish(null);
  }, [publish, internalRedirectUrl]);
}
